import type { AirEvent, AirRoute, AwbTrackingData } from '@aircargomcp/types'
import { STATUS_LABEL, fmtDate, fmtDateTime } from '../lib/format'

const FAMILY_LABEL: Record<string, string> = {
  ACCEPTANCE: 'Acceptance',
  FLIGHT: 'Flight',
  CUSTOMS: 'Customs',
  DELIVERY: 'Delivery',
}

// Status pill: green when delivered, cobalt while in motion.
function StatusPill({ status }: { status: string }) {
  const done = status === 'delivered'
  return (
    <span
      className={`mono inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
        done ? 'bg-up/15 text-up' : 'bg-signal-soft text-signal'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${done ? 'bg-up' : 'bg-signal'}`} />
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}

// A lane node: IATA code over the station name.
function Node({
  code,
  name,
  align,
}: { code?: string | null | undefined; name?: string | null | undefined; align: 'l' | 'r' }) {
  return (
    <div className={align === 'r' ? 'text-right' : ''}>
      <div className="mono text-xl font-extrabold tracking-tight text-ink tnum">{code ?? '—'}</div>
      {name && <div className="truncate text-xs text-muted">{name}</div>}
    </div>
  )
}

// One Cargo-IMP milestone in the timeline. Filled cobalt dot = actual, hollow = planned/estimated.
function EventRow({ ev, last }: { ev: AirEvent; last: boolean }) {
  return (
    <li className="relative flex gap-4 pb-5 last:pb-0">
      {/* connector rail */}
      {!last && (
        <span className="absolute left-[7px] top-4 h-full w-px bg-border-strong" aria-hidden />
      )}
      <span
        className={`relative z-10 mt-1 grid h-[15px] w-[15px] shrink-0 place-items-center rounded-full border-2 ${
          ev.is_actual ? 'border-signal bg-signal' : 'border-border-strong bg-surface-0'
        }`}
        aria-hidden
      >
        {ev.is_actual && <span className="h-1 w-1 rounded-full bg-void" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <span className="font-semibold text-ink">
            {ev.description || ev.status_code}
            <span className="mono ml-2 rounded bg-surface-1 px-1.5 py-0.5 text-[10px] font-semibold text-muted">
              {String(ev.status_code)}
            </span>
          </span>
          <span className="mono text-xs text-muted tnum">{fmtDateTime(ev.datetime)}</span>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted">
          <span className="uppercase tracking-wide text-faint">
            {FAMILY_LABEL[ev.event_family] ?? ev.event_family}
          </span>
          {ev.station && (
            <span>
              <span className="mono text-ink">{ev.station}</span>
              {ev.station_name ? ` · ${ev.station_name}` : ''}
            </span>
          )}
          {ev.flight_number && (
            <span>
              Flight <span className="mono text-ink">{ev.flight_number}</span>
            </span>
          )}
          {ev.pieces != null && <span>{ev.pieces} pcs</span>}
          {ev.weight_kg != null && <span className="tnum">{ev.weight_kg} kg</span>}
        </div>
      </div>
    </li>
  )
}

// One leg of the journey, straight off `routes[]`. Every line fills this now (the API derives legs from the
// milestones when a carrier does not publish them), so this block is never empty for a shipment with a lane.
// `path` on each leg is the great-circle polyline, ready for a map the day this page grows one.
const LEG_STATUS: Record<string, string> = {
  PLANNED: 'Planned',
  IN_TRANSIT: 'In the air',
  ARRIVED: 'Arrived',
}

function LegRow({ leg }: { leg: AirRoute }) {
  const dep = leg.departure?.actual ?? leg.departure?.estimated ?? null
  const arr = leg.arrival?.actual ?? leg.arrival?.estimated ?? null
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 border-b border-border py-2 last:border-0">
      <span className="flex items-baseline gap-2">
        <span className="mono text-sm font-semibold text-ink">{leg.from?.iata_code ?? '—'}</span>
        <span className="text-faint">to</span>
        <span className="mono text-sm font-semibold text-ink">{leg.to?.iata_code ?? '—'}</span>
        {leg.flight_number && (
          <span className="mono rounded bg-surface-1 px-1.5 py-0.5 text-[10px] font-semibold text-muted">
            {leg.flight_number}
          </span>
        )}
      </span>
      <span className="flex flex-wrap gap-x-3 text-xs text-muted">
        <span className="uppercase tracking-wide text-faint">
          {LEG_STATUS[String(leg.status)] ?? leg.status ?? 'Planned'}
        </span>
        {dep && <span className="tnum">Dep {fmtDateTime(dep)}</span>}
        {arr && <span className="tnum">Arr {fmtDateTime(arr)}</span>}
      </span>
    </li>
  )
}

export function AwbTimeline({ data }: { data: AwbTrackingData }) {
  return (
    <div className="card p-5 sm:p-6">
      {/* header: AWB + carrier + status */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.18em] text-faint">Air waybill</div>
          <div className="mono mt-0.5 text-lg font-bold text-ink tnum">{data.awb}</div>
          <div className="mt-1 text-sm text-muted">
            <span className="mono text-ink">{data.airline_iata}</span> · {data.airline_name}
          </div>
        </div>
        <StatusPill status={data.status} />
      </div>

      {/* lane */}
      <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl border border-border bg-surface-1/60 p-4">
        <Node code={data.origin} name={data.origin_name} align="l" />
        <div className="flex flex-col items-center px-2">
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            className="text-signal"
            fill="currentColor"
            aria-hidden
          >
            <path d="M22 16.5 14 13V6.5a2 2 0 0 0-4 0V13l-8 3.5V18l8-2.2V20l-2 1.3V22l3.5-1 3.5 1v-.7L14 20v-4.2l8 2.2v-1.5Z" />
          </svg>
          <div className="mono mt-1 text-[10px] uppercase tracking-wide text-faint">
            {data.eta_is_actual ? 'Arrived' : 'ETA'} {fmtDate(data.eta)}
          </div>
        </div>
        <Node code={data.destination} name={data.destination_name} align="r" />
      </div>

      {/* summary figures */}
      <div className="mt-4 grid grid-cols-3 gap-2 border-y border-border py-3 text-center">
        {[
          ['Pieces', data.pieces != null ? String(data.pieces) : '—'],
          ['Weight', data.weight_kg != null ? `${data.weight_kg} kg` : '—'],
          ['Events', String(data.events.length)],
        ].map(([label, val]) => (
          <div key={label}>
            <div className="mono text-[10px] uppercase tracking-wide text-faint">{label}</div>
            <div className="mono text-base font-semibold text-ink tnum">{val}</div>
          </div>
        ))}
      </div>

      {/* legs */}
      {data.routes && data.routes.length > 0 && (
        <div className="mt-5">
          <div className="mono mb-2 text-[11px] uppercase tracking-[0.18em] text-faint">
            Routing
          </div>
          <ol>
            {data.routes.map((leg, i) => (
              <LegRow key={`${leg.flight_number ?? 'leg'}-${leg.order_id ?? i}`} leg={leg} />
            ))}
          </ol>
        </div>
      )}

      {/* timeline */}
      <div className="mt-5">
        <div className="mono mb-3 text-[11px] uppercase tracking-[0.18em] text-faint">
          Cargo-IMP timeline
        </div>
        <ol>
          {data.events.map((ev, i) => (
            <EventRow
              key={`${ev.status_code}-${ev.datetime}-${i}`}
              ev={ev}
              last={i === data.events.length - 1}
            />
          ))}
        </ol>
      </div>
    </div>
  )
}
